/**
 * Control Portal - Billing & Licensing
 * View and manage device licenses, subscription status
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { controlApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  ArrowLeft, CreditCard, Clock, RefreshCw, Loader2, AlertTriangle,
  Check, X, Pause, Play, Plus, Calendar, Shield
} from 'lucide-react';

export default function TenantBillingPage() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [summary, setSummary] = useState({});
  const [licenses, setLicenses] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  
  // Dialog states
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [extendDays, setExtendDays] = useState(30);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [tenantRes, summaryRes, licensesRes] = await Promise.all([
        controlApi.getTenant(tenantId),
        controlApi.getBillingSummary(tenantId),
        controlApi.getLicenses(tenantId, { 
          limit: 100,
          ...(statusFilter && statusFilter !== 'all' && { status: statusFilter }),
          ...(planFilter && planFilter !== 'all' && { plan_code: planFilter })
        })
      ]);
      setTenant(tenantRes.data);
      setSummary(summaryRes.data);
      setLicenses(licensesRes.data);
    } catch (error) {
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, [tenantId, statusFilter, planFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openExtendDialog = (license) => {
    setSelectedLicense(license);
    setExtendDays(30);
    setExtendDialogOpen(true);
  };

  const handleExtend = async () => {
    setSaving(true);
    try {
      await controlApi.extendLicense(tenantId, selectedLicense.id, { days: extendDays });
      toast.success(`License extended by ${extendDays} days`);
      setExtendDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extend license');
    } finally {
      setSaving(false);
    }
  };

  const handleSuspend = async (license) => {
    if (!confirm(`Suspend license for ${license.device_uid}?`)) return;
    try {
      await controlApi.suspendLicense(tenantId, license.id);
      toast.success('License suspended');
      fetchData();
    } catch (error) {
      toast.error('Failed to suspend license');
    }
  };

  const handleReactivate = async (license) => {
    try {
      await controlApi.reactivateLicense(tenantId, license.id);
      toast.success('License reactivated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate license');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'grace': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'suspended': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'expired': return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  const getDaysColor = (days) => {
    if (days === null) return 'text-slate-400';
    if (days <= 0) return 'text-red-400';
    if (days <= 7) return 'text-amber-400';
    if (days <= 30) return 'text-yellow-400';
    return 'text-green-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="tenant-billing-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/control/tenants/${tenantId}`)}
            className="text-slate-400"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white">Billing & Licensing</h1>
            <p className="text-slate-400">{tenant?.name} - Device License Management</p>
          </div>
        </div>
        <Button variant="outline" onClick={fetchData} className="border-slate-700 text-slate-300">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-blue-500/20">
              <CreditCard className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{summary.total_devices || 0}</p>
              <p className="text-slate-400 text-xs">Total Devices</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-green-500/20">
              <Check className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{summary.active_licenses || 0}</p>
              <p className="text-slate-400 text-xs">Active</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-amber-500/20">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{summary.expiring_7_days || 0}</p>
              <p className="text-slate-400 text-xs">Expiring 7d</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-yellow-500/20">
              <Calendar className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{summary.expiring_30_days || 0}</p>
              <p className="text-slate-400 text-xs">Expiring 30d</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-orange-500/20">
              <AlertTriangle className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{summary.in_grace_period || 0}</p>
              <p className="text-slate-400 text-xs">Grace</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-red-500/20">
              <Pause className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{summary.suspended || 0}</p>
              <p className="text-slate-400 text-xs">Suspended</p>
            </div>
          </div>
        </div>
        <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-slate-700">
              <X className="w-5 h-5 text-slate-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{summary.expired || 0}</p>
              <p className="text-slate-400 text-xs">Expired</p>
            </div>
          </div>
        </div>
      </div>

      {/* Plan Breakdown */}
      {summary.plan_breakdown && Object.keys(summary.plan_breakdown).length > 0 && (
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-4 mb-6">
          <h3 className="text-white font-semibold mb-3">Active Licenses by Plan</h3>
          <div className="flex flex-wrap gap-3">
            {Object.entries(summary.plan_breakdown).map(([plan, count]) => (
              <div key={plan} className="bg-slate-800 rounded-lg px-4 py-2 flex items-center gap-2">
                <Shield className="w-4 h-4 text-blue-400" />
                <span className="text-white font-medium">{plan}</span>
                <Badge variant="secondary">{count}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4 mb-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] bg-slate-900 border-slate-700 text-white">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="all" className="text-white">All Statuses</SelectItem>
            <SelectItem value="active" className="text-white">Active</SelectItem>
            <SelectItem value="grace" className="text-white">Grace Period</SelectItem>
            <SelectItem value="suspended" className="text-white">Suspended</SelectItem>
            <SelectItem value="expired" className="text-white">Expired</SelectItem>
          </SelectContent>
        </Select>
        <Select value={planFilter} onValueChange={setPlanFilter}>
          <SelectTrigger className="w-[180px] bg-slate-900 border-slate-700 text-white">
            <SelectValue placeholder="All Plans" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="all" className="text-white">All Plans</SelectItem>
            <SelectItem value="BASIC" className="text-white">Basic</SelectItem>
            <SelectItem value="STANDARD" className="text-white">Standard</SelectItem>
            <SelectItem value="ENTERPRISE" className="text-white">Enterprise</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Licenses Table */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-800 hover:bg-slate-800/50">
              <TableHead className="text-slate-400">Device</TableHead>
              <TableHead className="text-slate-400">IMEI</TableHead>
              <TableHead className="text-slate-400">Plan</TableHead>
              <TableHead className="text-slate-400">Status</TableHead>
              <TableHead className="text-slate-400">Expires</TableHead>
              <TableHead className="text-slate-400 text-center">Days Left</TableHead>
              <TableHead className="text-slate-400 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {licenses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                  <CreditCard className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No licenses found</p>
                </TableCell>
              </TableRow>
            ) : (
              licenses.map((license) => (
                <TableRow key={license.id} className="border-slate-800 hover:bg-slate-800/50">
                  <TableCell className="text-white font-mono">{license.device_uid}</TableCell>
                  <TableCell className="text-slate-300 text-sm">{license.imei_number || '-'}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="border-blue-500/50 text-blue-400">
                      {license.plan_code}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(license.status)}>
                      {license.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-slate-300 text-sm">
                    {license.expires_at ? new Date(license.expires_at).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell className="text-center">
                    <span className={`font-bold ${getDaysColor(license.days_remaining)}`}>
                      {license.days_remaining !== null ? `${license.days_remaining}d` : '-'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openExtendDialog(license)}
                        className="text-green-400 hover:text-green-300"
                        title="Extend License"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                      {license.status === 'active' || license.status === 'grace' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSuspend(license)}
                          className="text-red-400 hover:text-red-300"
                          title="Suspend"
                        >
                          <Pause className="w-4 h-4" />
                        </Button>
                      ) : license.status === 'suspended' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleReactivate(license)}
                          className="text-blue-400 hover:text-blue-300"
                          title="Reactivate"
                        >
                          <Play className="w-4 h-4" />
                        </Button>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Extend License Dialog */}
      <Dialog open={extendDialogOpen} onOpenChange={setExtendDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Extend License</DialogTitle>
            <DialogDescription className="text-slate-400">
              Extend license for {selectedLicense?.device_uid}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300">Current Expiry</Label>
              <p className="text-white mt-1">
                {selectedLicense?.expires_at ? new Date(selectedLicense.expires_at).toLocaleDateString() : 'N/A'}
              </p>
            </div>
            <div>
              <Label className="text-slate-300">Extend by (days)</Label>
              <Input
                type="number"
                min={1}
                value={extendDays}
                onChange={(e) => setExtendDays(parseInt(e.target.value) || 0)}
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            <div className="flex gap-4">
              {[30, 90, 180, 365].map((days) => (
                <Button
                  key={days}
                  variant="outline"
                  size="sm"
                  onClick={() => setExtendDays(days)}
                  className={`border-slate-700 ${extendDays === days ? 'bg-blue-600 text-white' : 'text-slate-300'}`}
                >
                  {days}d
                </Button>
              ))}
            </div>
            <div className="flex gap-2 pt-4">
              <Button variant="ghost" onClick={() => setExtendDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleExtend} disabled={saving || extendDays <= 0} className="flex-1 bg-green-600 hover:bg-green-700">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Extend
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
